import {
  INGEST_PATH,
  SDK_VERSION,
  type Batch,
  type SessionContext,
  type WireEvent,
} from "@watchover/shared";
import type { WatchOverConfig } from "./config.js";
import { storageKey } from "./ids.js";
import { scrubUrl } from "./url.js";

/**
 * Buffers events and ships them in batches. Every design choice here protects
 * the host page's main thread:
 *
 *  • events are appended to an in-memory array (O(1), no layout/IO);
 *  • flushing prefers navigator.sendBeacon — fire-and-forget, off the critical
 *    path, and guaranteed to run during unload;
 *  • payloads are gzipped via CompressionStream (off-thread) when available;
 *  • a size/age threshold caps memory and latency without a tight timer;
 *  • we never throw into host code — all failures are swallowed/logged.
 */
export class Transport {
  private buffer: WireEvent[] = [];
  private bytes = 0;
  private seq = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private flushing = false;

  constructor(
    private ctx: SessionContext,
    private cfg: WatchOverConfig,
  ) {
    // Restore a monotonic sequence so replay chunks order correctly across
    // page loads within the same tab.
    try {
      this.seq = parseInt(sessionStorage.getItem(storageKey("wo_seq")) || "0", 10) || 0;
    } catch {
      /* ignore */
    }
  }

  start() {
    this.timer = setInterval(() => this.flush(false), this.cfg.flushIntervalMs);
    // Flush reliably when the tab is backgrounded or closed.
    const onHide = () => {
      if (document.visibilityState === "hidden") this.flush(true);
    };
    document.addEventListener("visibilitychange", onHide, { capture: true });
    window.addEventListener("pagehide", () => this.flush(true), {
      capture: true,
    });
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /** Update session/user context (e.g. after identify or session rotation). */
  updateContext(patch: Partial<SessionContext>) {
    this.ctx = { ...this.ctx, ...patch };
  }

  enqueue(event: WireEvent) {
    this.buffer.push(event);
    // Cheap byte estimate; avoids JSON.stringify on every enqueue.
    this.bytes += estimateSize(event);
    if (
      this.buffer.length >= this.cfg.maxBatchEvents ||
      this.bytes >= this.cfg.maxBatchBytes
    ) {
      // Defer to a microtask so a burst of enqueues coalesces into one flush.
      queueMicrotask(() => this.flush(false));
    }
  }

  get pending(): number {
    return this.buffer.length;
  }

  async flush(useBeacon: boolean): Promise<void> {
    if (this.flushing || this.buffer.length === 0) return;
    this.flushing = true;
    const events = this.buffer;
    this.buffer = [];
    this.bytes = 0;

    const batch: Batch = {
      ...this.ctx,
      sdk: SDK_VERSION,
      url: scrubUrl(location.href),
      seq: this.seq++,
      sentAt: Date.now(),
      events,
    };
    try {
      sessionStorage.setItem(storageKey("wo_seq"), String(this.seq));
    } catch {
      /* ignore */
    }

    const json = JSON.stringify(batch);
    const url = this.cfg.ingestUrl + INGEST_PATH;
    try {
      const body = await maybeGzip(json);
      const sent = this.send(url, body, useBeacon);
      if (!sent) {
        // Re-queue on hard failure so nothing is silently dropped (unless we're
        // unloading, where there's no next flush — beacon was best-effort).
        if (!useBeacon) this.requeue(events);
      }
    } catch {
      if (!useBeacon) this.requeue(events);
    } finally {
      this.flushing = false;
    }
  }

  private requeue(events: WireEvent[]) {
    // Prepend so ordering is preserved; cap to avoid unbounded growth.
    this.buffer = [...events, ...this.buffer].slice(0, this.cfg.maxBatchEvents * 4);
    for (const e of events) this.bytes += estimateSize(e);
  }

  private send(url: string, body: BodyPayload, useBeacon: boolean): boolean {
    // sendBeacon: best for unload + never blocks. Body is a Blob/string.
    if (useBeacon && navigator.sendBeacon) {
      try {
        return navigator.sendBeacon(url, body as BodyInit);
      } catch {
        /* fall through to fetch */
      }
    }
    if (navigator.sendBeacon && !useBeacon) {
      try {
        if (navigator.sendBeacon(url, body as BodyInit)) return true;
      } catch {
        /* fall through */
      }
    }
    // Fetch keepalive fallback (also survives unload, with size limits).
    try {
      void fetch(url, {
        method: "POST",
        body: body as BodyInit,
        keepalive: true,
        // text/plain keeps it a CORS-simple request (no preflight).
        headers: { "Content-Type": "text/plain" },
        credentials: "omit",
        mode: "cors",
      }).catch(() => {});
      return true;
    } catch {
      return false;
    }
  }
}

type BodyPayload = Blob | string;

function estimateSize(event: WireEvent): number {
  // Replay frames dominate; estimate from record count, else a flat guess.
  if (event.t === "replay") return (event.records?.length ?? 0) * 256 + 64;
  return 96;
}

/** gzip the payload off the main thread when CompressionStream is supported. */
async function maybeGzip(json: string): Promise<BodyPayload> {
  const CS = (globalThis as any).CompressionStream;
  if (!CS) return json;
  try {
    const stream = new Blob([json]).stream().pipeThrough(new CS("gzip"));
    // Tagged text/plain so sendBeacon/fetch stay CORS-simple; the server sniffs
    // the gzip magic bytes rather than trusting a header.
    const buf = await new Response(stream).arrayBuffer();
    return new Blob([buf], { type: "text/plain" });
  } catch {
    return json;
  }
}
